import React from 'react';
import Icon from '../../../components/AppIcon';

const ProfessionalBiography = () => {
  const expertise = [
    {
      icon: 'Code2',
      title: 'MERN Stack Development',
      description: 'End-to-end application design with React.js, Next.js, Node.js, Express.js, MongoDB, and REST APIs.',
      color: 'primary'
    },
    {
      icon: 'Server',
      title: 'API Development & Documentation',
      description: 'Built secure REST APIs with JWT, RBAC, and Swagger documentation, ensuring clarity for teams & clients. TypeScript is used for type safety and better code organization.',
      color: 'success'
    },
    {
      icon: 'Globe',
      title: 'WordPress Development',
      description: 'Expert in custom plugin development, theme customization, and headless WordPress integration with React/Next.js.',
      color: 'accent'
    },
    {
      icon: 'Layers',
      title: 'SaaS Product Development',
      description: null,
      color: 'primary',
      customContent: true
    },
    {
      icon: 'Users',
      title: 'Client Customizations',
      description: 'Delivered 12+ KiviCare-based client projects, including appointment modules, custom payment gateways, and clinic dashboards.',
      color: 'warning',
      hasLinks: true
    },
    {
      icon: 'ShoppingCart',
      title: 'Headless WooCommerce',
      description: 'Created Woo Headless API Plugin, a complete REST backend for WooCommerce, enabling headless eCommerce with Next.js & React.',
      color: 'success'
    },
    {
      icon: 'Cloud',
      title: 'DevOps & Cloud',
      description: 'Skilled in Docker, GitHub workflows, CI/CD, and cloud deployment (Vercel, AWS basics).',
      color: 'accent'
    }
  ];

  const highlights = [
    {
      icon: 'Award',
      title: 'Technical Leadership',
      description: 'Led development teams of 5-8 engineers across multiple high-impact projects'
    },
    {
      icon: 'TrendingUp',
      title: 'Performance Optimization',
      description: 'Improved application performance by 40% through strategic code refactoring'
    },
    {
      icon: 'Users',
      title: 'Mentorship',
      description: 'Mentored 5+ junior developers, with 100% promotion rate within 6 months'
    },
    {
      icon: 'Zap',
      title: 'Innovation',
      description: 'Pioneered adoption of modern frameworks, reducing development time by 30%'
    }
  ];

  const values = [
    {
      icon: 'Code',
      title: 'Clean Code Advocate',
      description: 'Passionate about writing maintainable, scalable, and well-documented code'
    },
    {
      icon: 'Users',
      title: 'Collaborative Spirit',
      description: 'Believe in the power of teamwork and cross-functional collaboration'
    },
    {
      icon: 'BookOpen',
      title: 'Continuous Learning',
      description: 'Committed to staying current with emerging technologies and best practices'
    },
    {
      icon: 'Target',
      title: 'Results-Driven',
      description: 'Focus on delivering measurable business value through technical solutions'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Current Role Summary */}
      <div className="bg-surface rounded-lg p-6 shadow-md border border-border">
        <div className="flex items-center mb-4">
          <h2 className="text-2xl font-bold text-text-primary">Current Role</h2>
        </div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-text-primary mb-3 break-words">Senior MERN Stack Developer | Technical Consultant</h3>
          <div className="flex items-center justify-between">
            <div className="flex items-center text-primary font-medium">
              <Icon name="Building" size={16} className="mr-2" />
              Cosmic Info Solution
            </div>
            <div className="flex items-center text-text-secondary">
              <Icon name="Calendar" size={16} className="mr-2" />
              2023 - Present
            </div>
          </div>
        </div>
        <p className="text-text-secondary leading-relaxed mb-4">
          Currently serving as the primary technical point of contact for clients, leading discovery calls and translating workflows into actionable software requirements. 
          Specializing in React ecosystem, Node.js backend services, and cloud infrastructure on AWS. 
          Responsible for architectural decisions, project lifecycle management, and providing ongoing technical consulting.
        </p>
        <div className="flex flex-wrap gap-2">
          {['React', 'Next.js', 'Node.js', 'TypeScript', 'AWS', 'MongoDB', 'MySQL', 'Docker', 'WordPress', 'KiviCare', 'WP Bookit', 'SocialV', 'Streamit'].map((tech) => (
            <span key={tech} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
              {tech}
            </span>
          ))}
        </div>
      </div>

      {/* Professional Biography */}
      <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-6 shadow-md border border-primary/20">
        <div className="flex items-center mb-4">
          <h2 className="text-2xl font-bold text-text-primary">About Me</h2>
        </div>
        <div className="prose prose-slate max-w-none">
          <p className="text-text-secondary leading-relaxed mb-4">
            I have spent the last 4.5+ years building enterprise web applications and leading complete software projects from concept to production.
          </p>
          <p className="text-text-secondary leading-relaxed mb-4">
            Throughout my career, I've worked closely with startups, agencies, and business owners to understand their operational challenges and convert them into scalable software solutions.
          </p>
          <p className="text-text-secondary leading-relaxed mb-4">
            My expertise extends beyond coding. I actively participate in product planning, business analysis, solution architecture, client communication, infrastructure planning, deployment strategy, and long-term product evolution.
          </p>
          <p className="text-text-secondary leading-relaxed">
            I believe great software is built by understanding business problems first and writing code second.
          </p>
        </div>
      </div>

      {/* My Expertise */}
      <div className="bg-surface rounded-lg p-6 shadow-md border border-border">
        <div className="flex items-center mb-6">
          <h2 className="text-2xl font-bold text-text-primary">My Expertise</h2>
        </div>
        <div className="space-y-6">
          {expertise.map((item, index) => (
            <div key={index} className="flex items-start space-x-4">
              <div className={`w-10 h-10 rounded-lg bg-${item.color}/10 flex items-center justify-center flex-shrink-0`}>
                <Icon name={item.icon} size={20} className={`text-${item.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-text-primary mb-2">{item.title}</h3>
                {item.customContent && item.title === 'SaaS Product Development' ? (
                  <p className="text-text-secondary text-sm leading-relaxed">
                    Core developer of{' '}
                    <a 
                      href="https://demo.kivicare.io/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 underline"
                    >
                      KiviCare
                    </a>
                    ,{' '}
                    <a 
                      href="https://demo-wpbookit.iqonic.design/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 underline"
                    >
                      WP Bookit
                    </a>
                    ,{' '}
                    <a 
                      href="https://socialv-wordpress.iqonic.design/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 underline"
                    >
                      SocialV
                    </a>
                    , and{' '}
                    <a 
                      href="https://streamit.iqonic.design/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 underline"
                    >
                      Streamit
                    </a>
                    {' '}at Iqonic Design, used by global clients.
                  </p>
                ) : item.hasLinks && item.title === 'Client Customizations' ? (
                  <>
                    <p className="text-text-secondary text-sm leading-relaxed mb-3">
                      {item.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <a 
                        href="https://global.clinic/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Global Clinic
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://primaclinic.it/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Primaclinic
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://doctorisiterapeuti.ro/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Doctori si Terapeuti
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://healthyuksa.com/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Healthy UKSA
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://kushmedicare.com/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Kush Medicare
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://conexionmed.com/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Conexion Med
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://thchealth.link/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        THC Health
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://scripify.com.au/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Scripify
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://www.orthocarestp.it/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Orthocare STP
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://globalposeidon.org/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Global Poseidon
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://zapmed.co.za/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        ZapMed
                        <Icon name="ExternalLink" size={12} />
                      </a>
                      <span className="text-text-secondary text-xs">•</span>
                      <a 
                        href="https://antigua-med.com/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1 text-xs"
                      >
                        Antigua Med
                        <Icon name="ExternalLink" size={12} />
                      </a>
                    </div>
                  </>
                ) : (
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {item.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why Work With Me */}
      <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-6 shadow-md border border-primary/20">
        <div className="flex items-center mb-6">
          <h2 className="text-2xl font-bold text-text-primary">Why Work With Me?</h2>
        </div>
        <p className="text-text-secondary leading-relaxed mb-6 text-lg">
          I combine product-level experience with client-facing customization skills, giving me a unique edge: I not only build scalable SaaS solutions but also adapt them to meet real business needs.
        </p>
      </div>

      {/* Career Highlights */}
      <div className="bg-surface rounded-lg p-6 shadow-md border border-border">
        <div className="flex items-center mb-6">
          <h2 className="text-2xl font-bold text-text-primary">Career Highlights</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {highlights.map((highlight, index) => (
            <div key={index} className="flex items-start space-x-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={highlight.icon} size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-text-primary mb-1">{highlight.title}</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{highlight.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Professional Philosophy */}
      <div className="bg-surface rounded-lg p-6 shadow-md border border-border">
        <div className="flex items-center mb-6">
          <h2 className="text-2xl font-bold text-text-primary">Values & Philosophy</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {values.map((value, index) => (
            <div key={index} className="flex items-start space-x-4">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Icon name={value.icon} size={20} className="text-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-text-primary mb-1">{value.title}</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{value.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Personal Interests */}
      <div className="bg-surface rounded-lg p-6 shadow-md border border-border">
        <div className="flex items-center mb-4">
          <h2 className="text-2xl font-bold text-text-primary">Beyond Code</h2>
        </div>
        <p className="text-text-secondary leading-relaxed mb-4">
        I enjoy contributing to open-source projects, experimenting with headless CMS architectures, and building portfolio projects like my E-commerce Platform (React frontend + Node.js REST APIs with Swagger documentation). I also explore the AI industry and leverage modern AI development tools to accelerate workflows and enable faster project delivery.
        </p>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center px-3 py-2 bg-muted rounded-lg">
            <span className="text-text-secondary text-sm">Open Source</span>
          </div>
          <div className="flex items-center px-3 py-2 bg-muted rounded-lg">
            <span className="text-text-secondary text-sm">AI Tools</span>
          </div>
          <div className="flex items-center px-3 py-2 bg-muted rounded-lg">
            <span className="text-text-secondary text-sm">Quick Delivery</span>
          </div>
          <div className="flex items-center px-3 py-2 bg-muted rounded-lg">
            <span className="text-text-secondary text-sm">Headless CMS</span>
          </div>
          <div className="flex items-center px-3 py-2 bg-muted rounded-lg">
            <span className="text-text-secondary text-sm">Tech Innovation</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalBiography;